import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../keys";
import {
  getAppealsAPI,
  getCommentsReportsAPI,
  getFlaggedContentAPI,
  getModerationStatsAPI,
  getPostReportsAPI,
  getProfileReportsAPI,
} from "../../api/moderationApi";
import { getTokens } from "../../utils/tokens";

const MODERATION_STALE_MS = 30 * 1000;

const EMPTY_PAGED = {
  items: [] as unknown[],
  totalCount: 0,
  currentPage: 1,
  totalPages: 0,
};

export type ModerationFilters = {
  status: string;
  severity: string;
  contentType: string;
  sortOrder: string;
};

export type ModerationReportFilters = {
  status: string;
  postId: string;
  profileId: string;
  appealId: string;
};

export type ModerationAppealFilters = {
  status: string;
  targetId: string;
  userId: string;
};

export function useModerationPageQueries(
  activeTab: number,
  filters: ModerationFilters,
  reportFilters: ModerationReportFilters,
  appealFilters: ModerationAppealFilters
) {
  const hasToken = !!getTokens()?.access_token;

  const postReportsParams = useMemo(
    () => ({
      ...reportFilters,
      page: 1,
      perPage: 25,
    }),
    [reportFilters]
  );

  const profileReportsParams = useMemo(
    () => ({
      ...reportFilters,
      page: 1,
      perPage: 25,
    }),
    [reportFilters]
  );

  const commentReportsParams = useMemo(
    () => ({
      ...reportFilters,
      page: 1,
      perPage: 25,
    }),
    [reportFilters]
  );

  const appealsParams = useMemo(
    () => ({
      ...appealFilters,
      page: 1,
      perPage: 25,
    }),
    [appealFilters]
  );

  const flaggedTabQuery = useQuery({
    queryKey: queryKeys.moderation.flaggedTab(filters),
    queryFn: async () => {
      const [contentResponse, statsResponse] = await Promise.all([
        getFlaggedContentAPI(filters),
        getModerationStatsAPI(),
      ]);
      return {
        flagged: contentResponse.success
          ? contentResponse.data
          : { ...EMPTY_PAGED },
        stats: statsResponse.success ? statsResponse.data : null,
      };
    },
    enabled: hasToken && activeTab === 0,
    staleTime: MODERATION_STALE_MS,
  });

  const postReportsQuery = useQuery({
    queryKey: queryKeys.moderation.postReports(postReportsParams),
    queryFn: async () => {
      const response = await getPostReportsAPI(postReportsParams);
      if (!response.success) {
        throw new Error(
          (response as { message?: string }).message ||
            "Failed to load post reports"
        );
      }
      return response.data;
    },
    enabled: hasToken && activeTab === 1,
    staleTime: MODERATION_STALE_MS,
  });

  const profileReportsQuery = useQuery({
    queryKey: queryKeys.moderation.profileReports(profileReportsParams),
    queryFn: async () => {
      const response = await getProfileReportsAPI(profileReportsParams);
      if (!response.success) {
        throw new Error(
          (response as { message?: string }).message ||
            "Failed to load profile reports"
        );
      }
      return response.data;
    },
    enabled: hasToken && activeTab === 2,
    staleTime: MODERATION_STALE_MS,
  });

  const commentReportsQuery = useQuery({
    queryKey: queryKeys.moderation.commentReports(commentReportsParams),
    queryFn: async () => {
      const response = await getCommentsReportsAPI(commentReportsParams);
      if (!response.success) {
        throw new Error(
          (response as { message?: string }).message ||
            "Failed to load comment reports"
        );
      }
      return response.data;
    },
    enabled: hasToken && activeTab === 3,
    staleTime: MODERATION_STALE_MS,
  });

  const appealsQuery = useQuery({
    queryKey: queryKeys.moderation.appeals(appealsParams),
    queryFn: async () => {
      const response = await getAppealsAPI(appealsParams);
      if (!response.success) {
        throw new Error(
          (response as { message?: string }).message || "Failed to load appeals"
        );
      }
      return response.data;
    },
    enabled: hasToken && activeTab === 4,
    staleTime: MODERATION_STALE_MS,
  });

  const loading =
    (activeTab === 0 && flaggedTabQuery.isPending) ||
    (activeTab === 1 && postReportsQuery.isPending) ||
    (activeTab === 2 && profileReportsQuery.isPending) ||
    (activeTab === 3 && commentReportsQuery.isPending) ||
    (activeTab === 4 && appealsQuery.isPending);

  return {
    flaggedContent: flaggedTabQuery.data?.flagged ?? EMPTY_PAGED,
    stats: flaggedTabQuery.data?.stats ?? null,
    postReports: postReportsQuery.data ?? EMPTY_PAGED,
    profileReports: profileReportsQuery.data ?? EMPTY_PAGED,
    commentReports: commentReportsQuery.data ?? EMPTY_PAGED,
    appeals: appealsQuery.data ?? EMPTY_PAGED,
    loading,
    flaggedTabQuery,
    postReportsQuery,
    profileReportsQuery,
    commentReportsQuery,
    appealsQuery,
  };
}
